import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, PlayCircle, ShieldAlert, SlidersHorizontal } from "lucide-react";
import platformApi from "@/lib/platformApi";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";

const GOLD_INK = "var(--brand-accent-ink)";
export const SEVERITY_STYLE = {
  critical: "bg-red-50 border-red-300 text-red-700",
  high: "bg-orange-50 border-orange-200 text-orange-700",
  medium: "bg-amber-50 border-amber-200 text-amber-700",
  low: "bg-slate-100 border-slate-300 text-slate-600",
};

export default function AnomalyPanel({ onAcknowledged }) {
  const [rules, setRules] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [ackBusy, setAckBusy] = useState(null);
  const [savingRule, setSavingRule] = useState(null);

  const loadRules = () => platformApi.get("/anomaly/rules").then((r) => setRules(r.data.rules));
  const loadAlerts = () => platformApi.get(`/anomaly/alerts?status=${showAll ? "all" : "active"}`)
    .then((r) => setAlerts(r.data.alerts));

  useEffect(() => { loadRules(); }, []); // eslint-disable-line
  useEffect(() => { loadAlerts(); }, [showAll]); // eslint-disable-line

  const evaluate = async () => {
    setEvaluating(true);
    try {
      const { data } = await platformApi.post("/anomaly/evaluate");
      toast.success(data.new_count
        ? `${data.new_count} new alert${data.new_count > 1 ? "s" : ""} fired`
        : "Evaluation complete — no new anomalies");
      loadAlerts(); onAcknowledged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setEvaluating(false);
    }
  };

  const acknowledge = async (id) => {
    setAckBusy(id);
    try {
      await platformApi.post(`/anomaly/alerts/${id}/acknowledge`);
      toast.success("Alert acknowledged");
      loadAlerts(); onAcknowledged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAckBusy(null);
    }
  };

  const saveRule = async (rule, patch) => {
    setSavingRule(rule.id);
    try {
      const { data } = await platformApi.put(`/anomaly/rules/${rule.id}`, patch);
      setRules(data.rules);
      toast.success(`${rule.label} updated`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      loadRules();
    } finally {
      setSavingRule(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="anomaly-panel">
      {/* alerts strip */}
      <div className="bg-white border border-slate-200" data-testid="anomaly-alerts-strip">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[12px] font-bold uppercase tracking-[0.14em] flex items-center gap-2 text-[#1E293B]">
            <ShieldAlert className="h-4 w-4" style={{ color: GOLD_INK }} /> Anomaly alerts
            {alerts && (
              <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-slate-100 border-slate-300 text-slate-600" data-testid="anomaly-active-count">
                {alerts.filter((a) => !a.acknowledged).length} active
              </span>
            )}
          </p>
          <div className="flex items-center gap-3">
            <label className="text-[10px] font-mono uppercase text-slate-400 flex items-center gap-1.5 cursor-pointer">
              <Switch checked={showAll} onCheckedChange={setShowAll} data-testid="anomaly-show-all" /> show acknowledged
            </label>
            <Button onClick={evaluate} disabled={evaluating} data-testid="anomaly-evaluate-btn"
              className="rounded-none h-8 text-[11px] bg-[#1E293B] hover:bg-slate-700 text-white">
              {evaluating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
              Evaluate now
            </Button>
          </div>
        </div>
        {!alerts ? <Skeleton className="h-24 w-full rounded-none" /> : alerts.length === 0 ? (
          <p className="text-[12px] text-slate-400 px-5 py-6 flex items-center gap-2" data-testid="anomaly-empty">
            <CheckCircle2 className="h-4 w-4 text-teal-600" /> No {showAll ? "" : "active "}alerts — all clear.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {alerts.map((a) => (
              <div key={a.id} data-testid={`anomaly-alert-${a.id}`}
                className={`px-5 py-3 flex items-center gap-3 ${a.acknowledged ? "opacity-55" : ""}`}>
                <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 border shrink-0 ${SEVERITY_STYLE[a.severity] || ""}`}>
                  {a.severity}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] text-[#1E293B] truncate">{a.summary}</p>
                  <p className="text-[10px] font-mono text-slate-400 mt-0.5">
                    {a.rule_label} · {a.tenant_id} · {new Date(a.created_at).toLocaleString("en-GB")}
                    {a.acknowledged ? ` · ack by ${a.acknowledged_by}` : ""}
                  </p>
                </div>
                {a.acknowledged ? (
                  <span className="text-[10px] font-mono uppercase text-slate-400 flex items-center gap-1 shrink-0">
                    <CheckCircle2 className="h-3.5 w-3.5" /> acknowledged
                  </span>
                ) : (
                  <button onClick={() => acknowledge(a.id)} disabled={ackBusy === a.id}
                    data-testid={`anomaly-ack-${a.id}`}
                    className="text-[10px] font-mono uppercase border border-slate-300 px-2 py-1 hover:bg-slate-50 disabled:opacity-50 shrink-0 flex items-center gap-1">
                    {ackBusy === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} acknowledge
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* rules editor */}
      <div className="bg-white border border-slate-200" data-testid="anomaly-rules-panel">
        <div className="px-5 py-3 border-b border-slate-200">
          <p className="text-[12px] font-bold uppercase tracking-[0.14em] flex items-center gap-2 text-[#1E293B]">
            <SlidersHorizontal className="h-4 w-4" style={{ color: GOLD_INK }} /> Detection rules
            <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-slate-100 border-slate-300 text-slate-600">
              root only — evaluated hourly
            </span>
          </p>
        </div>
        {!rules ? <Skeleton className="h-40 w-full rounded-none" /> : (
          <div className="divide-y divide-slate-100">
            {rules.map((r) => (
              <div key={r.id} data-testid={`anomaly-rule-${r.id}`}
                className="px-5 py-3 flex items-center gap-4 flex-wrap">
                <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 border shrink-0 ${SEVERITY_STYLE[r.severity] || ""}`}>
                  {r.severity}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-semibold text-[#1E293B] flex items-center gap-2">
                    {r.label}
                    {savingRule === r.id && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{r.description}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] font-mono uppercase text-slate-400">threshold</span>
                  <Input type="number" defaultValue={r.threshold} min={1} disabled={!r.enabled}
                    data-testid={`anomaly-threshold-${r.id}`}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (v >= 1 && v !== r.threshold) saveRule(r, { threshold: v });
                    }}
                    className="rounded-none h-8 w-20 font-mono text-[12px]" />
                  <span className="text-[10px] text-slate-400 w-40 leading-tight">{r.unit}</span>
                </div>
                <Switch checked={r.enabled} disabled={savingRule === r.id}
                  data-testid={`anomaly-toggle-${r.id}`}
                  onCheckedChange={(v) => saveRule(r, { enabled: v })} />
              </div>
            ))}
          </div>
        )}
        <p className="px-5 py-2.5 text-[10px] text-slate-400 border-t border-slate-100 flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3" /> Alerts are in-app only (no email while the mail provider awaits keys).
          Each rule fires once per subject per window — sustained conditions never spam.
        </p>
      </div>
    </div>
  );
}
